import {useTheme} from '@mui/material';
import React, {useEffect, useMemo, useState} from 'react';
import Joyride, {ACTIONS, EVENTS, STATUS} from 'react-joyride';
import {useDispatch, useSelector} from 'react-redux';

import {showLeftDrawer} from '../../actions/ui.js';
import TourCard from './TourCard.jsx';

import {endTour, setTourIndex, startTour} from '@app/actions/tour';
import {trackEvent} from '@app/analytics';
import {useGetInfiniteCollections} from '@app/api/collections';
import {DRAWERS, TOURS, TOUR_STAGES} from '@app/constants';

import useCollectionParams from '@app/hooks/useCollectionParams';
import useOrgParams from '@app/hooks/useOrgParams';

const Tour = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const {orgId} = useOrgParams();
  const tourState = useSelector(state => state.tour);
  const {setCollection, resetCollection} = useCollectionParams();

  const defaultOptions = {
    zIndex: 1500,
    arrowColor: (theme.vars || theme).palette.background.paper,
  };

  const [stepIndex, setStepIndex] = useState(tourState.index);

  const {data: collectionsData} = useGetInfiniteCollections(
    {orgId},
    {
      enabled: tourState.run && tourState.tour === TOURS.COLLECTION_TOUR_PT1,
    }
  );

  const firstCollection = useMemo(
    () => collectionsData?.collections[0],
    [collectionsData]
  );

  const setupTourStage = stageIndex => {
    const steps = tourState.steps;
    const stageId = steps[stageIndex] && steps[stageIndex].id;

    //Puppet MASTER!
    switch (stageId) {
      case TOUR_STAGES.UPLOAD_AOI:
        dispatch(showLeftDrawer(DRAWERS.COLLECTIONS));
        break;
      case TOUR_STAGES.OPEN_COLLECTION:
        resetCollection();
        break;
      case TOUR_STAGES.FEATURE_HEADER:
        setCollection(firstCollection.id);
        break;
      default:
        break;
    }

    dispatch(setTourIndex(stageIndex));
  };

  useEffect(() => {
    const stage = tourState.steps[tourState.index];
    const delay = stage && stage.delay ? stage.delay : 300;
    setTimeout(() => {
      setStepIndex(tourState.index);
    }, delay);
  }, [tourState.index, tourState.steps]);

  const onTourChange = data => {
    const {action, index, type, status, step} = data;

    if (
      tourState.tour === TOURS.COLLECTION_TOUR_PT1 &&
      status === STATUS.FINISHED
    ) {
      if (firstCollection.feature_count > 0) {
        dispatch(startTour(TOURS.COLLECTION_TOUR_PT2));
      } else {
        dispatch(endTour(tourState.tour));
      }
    }
    if (action === ACTIONS.CLOSE || [STATUS.FINISHED].includes(status)) {
      trackEvent(`Tour`, {
        action: 'Ended',
        status:
          status === STATUS.FINISHED || index + 1 === tourState.steps.length
            ? 'finished'
            : 'closed',
        step: index + 1,
      });

      if (step.onEnd) {
        step.onEnd();
      }

      dispatch(endTour(tourState.tour));
    } else if (
      [EVENTS.STEP_AFTER].includes(type) &&
      ![STATUS.SKIPPED].includes(status)
    ) {
      // Check whether next or back button click and update the step
      const newStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);

      setupTourStage(newStepIndex);
    }
  };

  return (
    <Joyride
      run={tourState.run}
      stepIndex={stepIndex}
      continuous
      disableCloseOnEsc
      disableScrolling
      disableOverlayClose
      steps={tourState.steps}
      tooltipComponent={TourCard}
      styles={{options: defaultOptions}}
      callback={onTourChange}
      floaterProps={{disableAnimation: true}}
    />
  );
};

export default Tour;
